import { createContext, memo, useContext } from "react";

import type { CalendarProps } from "../../calendar";
import type { CalendarBase } from "../../types/calendar-base";
import type { CalendarRangeProps, DateRange } from "../../types/calendar-range";
import type { CalendarSingleProps } from "../../types/calendar-single";

export type DaySelectionMode = "single" | "range";

export interface CalendarContextValue extends CalendarBase {
	mode: DaySelectionMode;
	onSelect?: CalendarSingleProps["onSelect"] | CalendarRangeProps["onSelect"];
	selected?: string | DateRange;
	numberOfMonths: number;
	hideYear: boolean;
	canSelectPastDate: boolean;
	showPresetOptions:
		| "not-show-preset-options" //
		| "exclude-past-options" //
		| "exclude-future-options"
		| "all-options";
}

export const CalendarContext = createContext<CalendarContextValue | undefined>(undefined);

type CalendarProviderProps = {
	initialProps: CalendarProps;
	children: React.ReactNode;
};

export const CalendarProvider = memo((props: CalendarProviderProps) => {
	const value: CalendarContextValue = {
		...props.initialProps,
		mode: props.initialProps.mode,
		numberOfMonths: props.initialProps.numberOfMonths ?? 1,
		hideYear: props.initialProps.hideYear ?? false,
		canSelectPastDate: props.initialProps.canSelectPastDate ?? false,
		showPresetOptions:
			"showPresetOptions" in props.initialProps
				? (props.initialProps.showPresetOptions ?? "not-show-preset-options")
				: "not-show-preset-options",
	};

	const { children } = props;

	return <CalendarContext.Provider value={value}>{children}</CalendarContext.Provider>;
});

CalendarProvider.displayName = "CalendarProvider";

export function useCalender(): CalendarContextValue {
	const context = useContext(CalendarContext);
	if (!context) {
		throw new Error("useCalender must be used within a CalendarProvider");
	}

	return context;
}
